import React from "react";
import { Helmet } from "react-helmet-async";

export default function BaumeApp() {
	const titlePage =
		"Baume App | Application mobile interne sur mesure | Weblax Studio";
	const description =
		"Weblax a conçu et développé Baume App, une application mobile interne React Native pour centraliser les outils et processus d'une entreprise en croissance. Dashboard temps réel, notifications push, gestion des tâches et suivi de projets.";
	const url = "https://weblax.fr/realisations/baume-app";

	const features = [
		{
			icon: "📊",
			title: "Dashboard centralisé",
			desc: "Vue d'ensemble en temps réel de toute l'activité : tâches en cours, projets actifs, alertes prioritaires et indicateurs clés — accessible en un seul coup d'œil.",
		},
		{
			icon: "🔔",
			title: "Notifications push temps réel",
			desc: "Système d'alertes intelligentes pour les événements critiques, les changements de statut et les tâches urgentes. Plus aucune information importante ne passe à la trappe.",
		},
		{
			icon: "✅",
			title: "Gestion des tâches",
			desc: "Interface simplifiée pour créer, assigner, prioriser et suivre les tâches par équipe. Réduction des doublons et meilleure coordination entre collaborateurs.",
		},
		{
			icon: "📁",
			title: "Ressources internes",
			desc: "Accès rapide aux documents, procédures et ressources partagées directement depuis l'app. Fini la chasse aux fichiers dans des boîtes mail surchargées.",
		},
		{
			icon: "📈",
			title: "Suivi de projets",
			desc: "Visualisation de l'avancement des projets, des jalons et des responsabilités. Chaque collaborateur sait où en sont les chantiers qui le concernent.",
		},
		{
			icon: "🔐",
			title: "Authentification sécurisée",
			desc: "Accès sécurisé par rôles et permissions. Les données sensibles restent protégées, avec une gestion fine des droits selon les profils collaborateurs.",
		},
	];

	const stack = [
		{ name: "React Native", desc: "App iOS & Android" },
		{ name: "Expo", desc: "Build & déploiement" },
		{ name: "Supabase", desc: "Backend & temps réel" },
		{ name: "Push Notifications", desc: "Alertes système" },
		{ name: "TypeScript", desc: "Fiabilité du code" },
		{ name: "Zustand", desc: "State management" },
		{ name: "FastAPI", desc: "API métier" },
	];

	const results = [
		{
			icon: "⏱️",
			metric: "-40%",
			label: "Temps perdu à chercher",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
		{
			icon: "📬",
			metric: "-65%",
			label: "Emails internes",
			color: "text-violet-600",
			bg: "from-violet-50 to-purple-50",
			border: "border-violet-100",
		},
		{
			icon: "📱",
			metric: "100%",
			label: "Adoption équipe J+15",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "🚀",
			metric: "+30%",
			label: "Productivité mesurée",
			color: "text-amber-600",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
	];

	const problems = [
		"Des informations dispersées entre emails, tableurs et messageries",
		"Aucune visibilité globale sur l'avancement des projets",
		"Des tâches en doublon faute de coordination centralisée",
		"Du temps perdu chaque jour à retrouver des documents",
		"Une communication interne chronophage et peu structurée",
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Baume App — Application mobile interne",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						programmingLanguage: ["TypeScript", "React Native"],
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-white to-blue-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 10% 50%, #ddd6fe 0%, transparent 50%), radial-gradient(circle at 90% 20%, #bfdbfe 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
							📱 Application mobile
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
							🏢 Usage interne
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Baume App — Le{" "}
						<span className="text-violet-600">hub opérationnel mobile</span> qui
						transforme la productivité interne
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a conçu et développé Baume App, une application mobile
						interne sur mesure en React Native. Un outil pensé pour mettre fin à
						la fragmentation des outils, réduire le bruit informationnel et
						donner à chaque collaborateur une vision claire et immédiate de ses
						priorités.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						La fragmentation des outils, fléau des entreprises modernes
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								L'entreprise Baume, en pleine phase de croissance, se retrouvait
								face à un problème que rencontrent beaucoup d'organisations à ce
								stade : l'accumulation d'outils disparates. Email pour
								communiquer, tableur pour suivre les projets, messagerie
								instantanée pour les urgences, dossiers partagés pour les
								documents — autant de silos qui nuisent à l'efficacité.
							</p>
							<p>
								Le résultat : des collaborateurs qui passent plus de temps à
								chercher l'information qu'à travailler dessus. Des tâches qui se
								perdent, des priorités mal communiquées, et un management qui
								manque de visibilité globale sur les opérations en cours.
							</p>
							<p>
								La réponse de Weblax : concevoir une application mobile interne
								sur mesure, pensée pour s'adapter aux processus existants de
								Baume plutôt que de les contraindre à s'adapter à un outil
								générique.
							</p>
						</div>
						<div>
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Problèmes identifiés avant Baume App
							</div>
							<div className="space-y-3">
								{problems.map((p, i) => (
									<div
										key={i}
										className="flex items-start gap-3 p-4 rounded-xl border border-border bg-red-50/50"
									>
										<span className="text-red-400 shrink-0 mt-0.5">✗</span>
										<span className="text-sm text-muted-foreground">{p}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</section>

				{/* ── Brief client ── */}
				<section className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Objectifs
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que Baume voulait accomplir
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🎯",
								text: "Un point d'accès unique pour tous les outils internes",
							},
							{
								icon: "📡",
								text: "Des notifications push intelligentes pour ne rien manquer",
							},
							{
								icon: "👁️",
								text: "Une visibilité temps réel sur les projets et priorités",
							},
							{
								icon: "⚡",
								text: "Une adoption rapide par tous les collaborateurs",
							},
							{
								icon: "📉",
								text: "Réduire drastiquement les emails et réunions inutiles",
							},
							{
								icon: "🔧",
								text: "Une solution évolutive, adaptable aux besoins futurs",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-violet-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Solution ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Solution
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Un hub mobile conçu autour des vrais usages
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Plutôt que d'imposer un outil standard, Weblax a commencé par
						analyser en profondeur les workflows existants de Baume. Chaque
						fonctionnalité a été validée avec les équipes avant d'être
						développée, pour s'assurer que l'app répond à de vrais besoins
						opérationnels — et non à des besoins imaginés.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-violet-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Processus de développement ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Développement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						Notre approche de développement agile
					</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								phase: "Phase 1",
								title: "Discovery & cadrage",
								desc: "Ateliers avec les équipes Baume pour cartographier les processus existants, identifier les frictions et prioriser les fonctionnalités à fort impact.",
								duration: "1 semaine",
							},
							{
								phase: "Phase 2",
								title: "Architecture & design",
								desc: "Conception de l'architecture technique, maquettes interactives et validation des parcours utilisateurs avec des testeurs internes Baume.",
								duration: "1 semaine",
							},
							{
								phase: "Phase 3",
								title: "Développement MVP",
								desc: "Développement des fonctionnalités core en React Native + Expo : dashboard, notifications, gestion des tâches et authentification sécurisée.",
								duration: "3 semaines",
							},
							{
								phase: "Phase 4",
								title: "Tests & déploiement",
								desc: "Tests multi-devices, validation par les équipes Baume, ajustements UX et déploiement sur TestFlight (iOS) et Play Store (Android).",
								duration: "1 semaine",
							},
						].map((item) => (
							<div
								key={item.phase}
								className="p-6 rounded-2xl border border-border bg-white"
							>
								<div className="flex items-center justify-between mb-3">
									<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
										{item.phase}
									</span>
									<span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
										{item.duration}
									</span>
								</div>
								<h3 className="font-semibold text-foreground mb-2">
									{item.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-4">
						Technologies choisies pour Baume App
					</h2>
					<p className="text-muted-foreground mb-8 max-w-2xl">
						Un stack moderne, éprouvé et orienté performance mobile — conçu pour
						fonctionner sur iOS et Android avec une base de code unique.
					</p>
					<div className="flex flex-wrap gap-3">
						{stack.map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Des gains mesurables dès les premières semaines
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						L'application a été adoptée à 100% par les équipes dans les deux
						premières semaines suivant le lancement — un indicateur qui reflète
						la qualité de l'UX et l'adéquation de la solution avec les besoins
						réels.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "-40%",
								detail: "de temps perdu à rechercher des informations",
								desc: "Les équipes accèdent instantanément à ce dont elles ont besoin, sans passer d'un outil à l'autre.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
							{
								metric: "-65%",
								detail: "de volume d'emails internes",
								desc: "Les communications sont centralisées dans l'app : moins de boîtes mail encombrées, moins de fils sans fin.",
								color: "text-violet-600",
								bg: "from-violet-50 to-purple-50",
								border: "border-violet-100",
							},
							{
								metric: "100%",
								detail: "d'adoption par les équipes à J+15",
								desc: "Aucune formation longue n'a été nécessaire — l'interface intuitive a permis une prise en main immédiate.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "+30%",
								detail: "de productivité mesurée",
								desc: "Moins de réunions, moins de tâches en doublon, une meilleure priorisation collective — le tout en quelques semaines.",
								color: "text-amber-600",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-violet-100 bg-gradient-to-br from-violet-50 to-blue-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">📱</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"En deux semaines, tout le monde utilisait l'app. Les réunions de
						suivi ont été réduites de moitié. Weblax a livré exactement ce qu'on
						imaginait — en mieux."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Direction Baume — Responsable opérations
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-violet-500" />
						<span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Une app interne bien conçue, c'est un avantage compétitif
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							Baume App démontre que l'optimisation interne est souvent un
							levier de performance sous-estimé. Avant même d'acquérir de
							nouveaux clients ou de lancer de nouveaux produits, rendre ses
							équipes plus efficaces génère des gains immédiats et durables.
						</p>
						<p>
							Une application mobile interne sur mesure n'est pas réservée aux
							grandes entreprises. Avec React Native et un stack moderne, Weblax
							peut livrer une solution robuste, scalable et adoptée rapidement —
							même pour des équipes de taille modeste.
						</p>
						<p>
							Le secret de Baume App ? Avoir commencé par écouter les équipes
							plutôt que par développer des fonctionnalités. C'est ce que Weblax
							fait systématiquement avant d'écrire la première ligne de code.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Vos équipes méritent un outil qui leur ressemble
							</h3>
							<p className="text-sm opacity-70">
								App mobile interne, dashboard, outil métier sur mesure — parlons
								de votre projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-violet-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
